<template>
  <div
    v-if="selected_report"
    id="navbar-sb-wrapper"
    class="navbar-sb-wrapper w-100"
    @mouseleave="hideMenu"
  >
    <b-navbar
      toggleable="lg"
      type="light"
      fixed="top"
      variant="white"
      class="navbar-sb w-100"
      :class="{ 'scrolled-navbar': isScrolled }"
    >
      <b-row class="flex-grow-1 d-flex justify-content-between padded-container" no-gutters>
        <b-col cols="4" xl="2" sxl="2" class="align-items-center">
          <b-navbar-brand class="cursor" @click="go_sb_main">
            <b-img
              id="sb-logo-img"
              class="navbar-sb-logo"
              :src="$store.state.static_host + selected_report.logo_path"
            />
          </b-navbar-brand>
        </b-col>

        <b-col
          v-if="!showSearchBar"
          class="d-none d-xl-flex justify-content-around justify-content-xl-center align-items-center"
          cols="8"
          @mouseover="showMenu"
        >
          <b-navbar-nav
            no-gutters
            class="d-none d-xl-flex navbar-sb-menu position-relative"
            :class="{ 'navbar-sb-menu-expanded': isHoverMainMenu }"
          >
            <b-nav-item
              v-for="(lv1_info, lv1_idx) in sb_toc"
              :key="'nav_lv1' + lv1_idx"
              href="#"
              class="flex-grow-1 nav-item-custom align-items-center"
              :link-classes="['flex-grow-1 fw-600 py-0 px-0 nav-link-equal']"
              @mouseover="
                handleHoverMenuItem(
                  lang === 'KOR'
                    ? lv1_info.name
                    : lv1_info.name_em
                    ? lv1_info.name_em
                    : lv1_info.name_ea
                )
              "
              @mouseleave="resetHoverMenuItem"
              @click="update_lv1_info(lv1_info)"
            >
              <span
                v-if="lang === 'KOR'"
                class="nav-link-custom"
                :class="isLv1Active(lv1_info.key) ? 'nav-link-custom-active' : ''"
              >
                {{ lv1_info.name }}
              </span>
              <span
                v-else
                class="nav-link-custom"
                :class="isLv1Active(lv1_info.key) ? 'nav-link-custom-active' : ''"
              >
                {{ lv1_info.name_em ? lv1_info.name_em : lv1_info.name_ea }}
              </span>
            </b-nav-item>
          </b-navbar-nav>
        </b-col>
        <b-col
          v-else
          class="d-none d-md-flex justify-content-around justify-content-center"
          cols="4"
          xl="8"
        >
          <b-navbar-nav no-gutters class="d-flex position-relative">
            <b-modal
              id="web-report-search-result"
              size="xl"
              centered
              scrollable
              title="검색 결과"
              @hide="closeSearch"
            >
              <SearchResult
                :search-keyword="searchKeyword"
                :search-results="searchResults"
                :loading="loading"
                @closeSearch="closeSearch"
              />
              <!-- Custom Footer -->
              <template #modal-footer>
                <b-button variant="outline-secondary" @click="closeSearch">닫기</b-button>
              </template>
            </b-modal>

            <b-input-group class="my-3 search-input-group">
              <b-form-input
                ref="report_search_form"
                v-model="searchKeyword"
                placeholder="키워드를 입력하세요..."
                class="search-input"
                @keyup.enter="performSearch(searchKeyword)"
              />
              <b-input-group-append>
                <b-button
                  variant="none"
                  class="search-button"
                  @click="performSearch(searchKeyword)"
                >
                  <b-icon icon="search" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-navbar-nav>
        </b-col>

        <b-col
          cols="4"
          sm="4"
          md="2"
          offset-md="2"
          offset-xl="0"
          class="d-flex justify-content-end align-items-center"
        >
          <b-navbar-nav
            class="d-flex flex-row justify-content-around justify-content-xl-end align-items-center w-100"
          >
            <b-nav-item class="d-xl-none">
              <b-btn
                v-b-tooltip.hover.bottom
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :title="lang != 'ENG' ? '메뉴' : 'Menu'"
                @click="toggleDropdown"
              >
                <svg
                  width="28"
                  height="28"
                  class="hamburger-icon"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="4" y="12" width="48" height="3" fill="currentColor" />
                  <rect x="4" y="27" width="48" height="3" fill="currentColor" />
                  <rect x="4" y="42" width="48" height="3" fill="currentColor" />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item>
              <b-btn
                v-b-tooltip.hover.bottom
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :title="lang != 'ENG' ? '검색하기' : 'Search'"
                @mouseover="handleHoverIcon('print')"
                @mouseleave="resetHoverIcon"
                @click="toggleSearchBar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 20 20"
                  style="transform: translateY(3px)"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"
                  />
                </svg>
              </b-btn>
            </b-nav-item>

            <b-nav-item
              v-if="theme_option?.theme_info?.show_printing_btn"
              class="d-none d-xl-block"
            >
              <b-btn
                v-b-tooltip.hover.bottom
                :disabled="!disclosures_loaded"
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :title="
                  !disclosures_loaded
                    ? 'pdf 출력을 준비중입니다.'
                    : lang != 'ENG'
                    ? '출력하기'
                    : 'Print to PDF'
                "
                @click="clickExportPdf"
                @mouseover="handleHoverIcon('print')"
                @mouseleave="resetHoverIcon"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9771 12.0494C17.9592 11.9191 17.8544 11.7555 17.742 11.6456C17.4199 11.3287 16.712 11.1626 15.6334 11.1472C14.9025 11.1396 14.0233 11.2035 13.0981 11.3338C12.684 11.0961 12.2572 10.8354 11.9224 10.5211C11.0202 9.6802 10.2688 8.5122 9.79851 7.22663C9.82918 7.10651 9.85473 7.00172 9.88029 6.89437C9.88029 6.89437 10.3863 4.01142 10.2534 3.0351C10.2355 2.9022 10.2228 2.86386 10.187 2.75908L10.1435 2.64662C10.0055 2.32714 9.73461 1.98978 9.31035 2.00767L9.05988 2H9.05477C8.57939 2 8.19601 2.2428 8.09378 2.60317C7.78709 3.73795 8.10401 5.43756 8.67906 7.63556L8.53083 7.99337C8.11934 9.00036 7.60307 10.0125 7.14813 10.9044L7.08935 11.0194C6.60886 11.9574 6.17437 12.7548 5.77822 13.4296L5.37185 13.6443C5.34118 13.6596 4.64344 14.0302 4.47987 14.1273C3.09207 14.9579 2.17197 15.8985 2.01863 16.6448C1.97007 16.885 2.00585 17.1892 2.25376 17.3297L2.64735 17.5291C2.81859 17.6134 3.00006 17.6569 3.18407 17.6569C4.17317 17.6569 5.32073 16.425 6.90278 13.6647C8.72762 13.0718 10.8081 12.5759 12.6303 12.305C14.0181 13.0845 15.7254 13.6289 16.804 13.6289C16.9957 13.6289 17.1618 13.611 17.2947 13.5753C17.5017 13.519 17.6755 13.4015 17.7828 13.243C17.9924 12.9286 18.0333 12.4941 17.9771 12.0494ZM2.96939 16.908C3.14829 16.4148 3.86392 15.4384 4.91947 14.5746C4.98592 14.5209 5.14949 14.3676 5.29773 14.2244C4.19362 15.9854 3.45499 16.6857 2.96939 16.908ZM9.22089 2.51116C9.53781 2.51116 9.71928 3.31113 9.73461 4.06254C9.74995 4.81395 9.57359 5.34044 9.35635 5.73148C9.17489 5.15387 9.08799 4.244 9.08799 3.6485C9.08799 3.6485 9.07521 2.51116 9.22089 2.51116ZM7.35515 12.7702C7.57751 12.374 7.80753 11.9549 8.04267 11.5127C8.61772 10.4265 8.98065 9.57797 9.24901 8.88024C9.78573 9.85655 10.4553 10.6872 11.24 11.3517C11.3397 11.4335 11.4444 11.5178 11.5518 11.6022C9.95441 11.9165 8.57172 12.3025 7.35515 12.7702ZM17.4327 12.6807C17.3356 12.7395 17.057 12.7753 16.8781 12.7753C16.3005 12.7753 15.5823 12.512 14.5779 12.0801C14.9638 12.052 15.3191 12.0367 15.636 12.0367C16.2161 12.0367 16.3874 12.0341 16.9573 12.1798C17.5247 12.3255 17.5324 12.6194 17.4327 12.6807Z"
                    fill="currentColor"
                  />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item v-if="theme_option?.theme_info?.show_translation_btn">
              <b-btn
                v-b-tooltip.hover.bottom
                :disabled="disableButtons"
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :title="lang != 'ENG' ? '영문보기' : 'Korean'"
                @click="toggleLang"
                @mouseover="handleHoverIcon('lang')"
                @mouseleave="resetHoverIcon"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="28" cy="28" r="21.5" stroke="currentColor" stroke-width="3" />
                  <path
                    d="M36.0837 28C36.0837 34.2027 35.0333 39.7479 33.3921 43.6865C32.5702 45.6591 31.6297 47.1575 30.662 48.1399C29.6995 49.1169 28.801 49.5 28.0003 49.5C27.1997 49.5 26.3011 49.1169 25.3386 48.1399C24.3709 47.1575 23.4304 45.6591 22.6085 43.6865C20.9674 39.7479 19.917 34.2027 19.917 28C19.917 21.7973 20.9674 16.2521 22.6085 12.3135C23.4304 10.3409 24.3709 8.84246 25.3386 7.86013C26.3011 6.88308 27.1997 6.5 28.0003 6.5C28.801 6.5 29.6995 6.88308 30.662 7.86013C31.6297 8.84246 32.5702 10.3409 33.3921 12.3135C35.0333 16.2521 36.0837 21.7973 36.0837 28Z"
                    stroke="currentColor"
                    stroke-width="3"
                  />
                  <path d="M6.91602 28H49.0827" stroke="currentColor" stroke-width="3" />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item v-if="$route.path === '/report_preview'">
              <b-button
                v-b-tooltip.hover
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'"
                @click="handleClosePreview"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  class="bi bi-x-lg"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"
                  />
                </svg>
              </b-button>
            </b-nav-item>
          </b-navbar-nav>
        </b-col>

        <b-modal
          id="story-board-modal"
          centered
          title="PDF 다운로드"
          hide-footer
          no-close-on-backdrop
          no-close-on-esc
          hide-header-close
        >
          <SbPrintOption />
        </b-modal>
      </b-row>
    </b-navbar>

    <search v-if="selected_lv1" />

    <!-- 목차 드롭다운 메뉴 -->
    <div
      v-if="showDropdown"
      class="submenu border-bottom"
      :class="{ 'submenu-slide-down': isHoverMainMenu, 'border-top': !isScrolled }"
    >
      <b-row no-gutters class="justify-content-center w-100 padded-container">
        <b-col cols="12" xl="8" class="d-none d-sm-flex justify-content-between">
          <div
            v-for="(lv1_info, lv1_idx) in sb_toc"
            :key="'dropdown_lv1' + lv1_idx"
            class="flex-grow-1 flex-column navbar-submenu justify-content-start nav-item-custom2 py-4 px-2"
          >
            <div
              v-for="(lv2_info, lv2_idx) in lv1_info.children"
              :key="'dropdown_lv2' + lv2_idx"
              @mouseover="handleHoverMenuItem2(lv1_info.name)"
              @mouseleave="resetHoverMenuItem2"
              @click="update_lv2_info(lv1_info, lv2_info)"
            >
              <div
                v-if="
                  lv2_info.appendix_type === null || report_types.includes(lv2_info.appendix_type)
                "
                class="link-container"
              >
                <b-link
                  class="submenu-link"
                  :class="{ 'disabled-link': isLv2Active(lv2_info.key) }"
                >
                  <span
                    v-if="lang === 'KOR'"
                    :class="[
                      {
                        hovered: hoveredLink === lv2_info.name,
                        'non-hovered': hoveredLink && hoveredLink !== lv2_info.name
                      },
                      isLv2Active(lv2_info.key) ? 'disabled-link-active' : ''
                    ]"
                  >
                    {{ lv2_info.name }}
                  </span>
                  <span
                    v-else
                    :class="[
                      {
                        hovered: hoveredLink === lv2_info.name,
                        'non-hovered': hoveredLink && hoveredLink !== lv2_info.name
                      },
                      isLv2Active(lv2_info.key) ? 'disabled-link-active' : ''
                    ]"
                  >
                    {{ lv2_info.name_em ? lv2_info.name_em : lv2_info.name_ea }}
                  </span>
                </b-link>
              </div>
            </div>
          </div>
        </b-col>

        <b-col cols="12" class="d-flex d-sm-none">
          <div class="accordion w-100" role="tablist">
            <div v-for="(lv1_info, lv1_idx) in sb_toc" :key="'dropdown_lv1_' + lv1_idx" class="">
              <div class="p-1 border-bottom-gray-900" role="tab">
                <b-button v-b-toggle="'accordion-' + lv1_idx" block variant="none">{{
                  lv1_info.name
                }}</b-button>
              </div>
              <b-collapse
                :id="'accordion-' + lv1_idx"
                role="tabpanel"
                :visible="lv1_idx === 0"
                accordion="my-accordion"
              >
                <div class="d-flex flex-column">
                  <b-button
                    v-for="(lv2_info, lv2_idx) in lv1_info.children"
                    :key="'dropdown_lv2_' + lv1_idx + '_' + lv2_idx"
                    :class="{ 'disabled-link': isLv2Active(lv2_info.key) }"
                    variant="light"
                    @click="update_lv2_info(lv1_info, lv2_info)"
                    >{{ lv2_info.name }}
                  </b-button>
                </div>
              </b-collapse>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import SbPrintOption from '@/components/report_preview/story_board/viewer_component/SbPrintOption.vue';
import search from '@/components/search_bar/search.vue';
import { updateParamsFromQueryString } from '../../../util';
import SearchResult from '../../../search_bar/SearchResult.vue';
export default {
  name: 'Nav',
  components: {
    SbPrintOption,
    search,
    SearchResult
  },
  computed: {
    ...mapGetters(['is_viewer']),
    ...mapState('assurance', ['assurance_status']),
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapGetters('story_board', [
      'selected_lv1',
      'selected_lv2',
      'sb_toc',
      'selected_viewer_disclosures'
    ]),
    ...mapState('story_board', ['disclosures_loaded', 'print_mode', 'viewer_disclosure_map']),
    ...mapState('report_contents', ['theme_option']),
    ...mapState('preview_route', ['globalString']),
    ...mapGetters('assurance', ['latest_assurance']),
    report_types() {
      const user_selected_report_type = Object.keys(this.selected_report.report_type).map(
        item => item.split('v')[0]
      );
      if (this.latest_assurance) {
        user_selected_report_type.push('ASSURANCE');
      }

      if (this.selected_report?.data_center) {
        user_selected_report_type.push('DATACENTER');
      }
      return user_selected_report_type;
    },
    mainColor() {
      const lv1Color = this.selected_lv1?.background_color;
      if (lv1Color && lv1Color !== '#014555') {
        return lv1Color;
      }
      return this.selected_report?.main_color || '#008C95';
    }
  },
  data() {
    return {
      showDropdown: true,
      hoveredMenuItem: null,
      hoveredIcon: null,
      hoveredLink: null,
      isHoverMainMenu: false,
      isScrolled: false,
      showSearchBar: false,
      searchKeyword: '',
      searchResults: null,
      loading: true,
      disableButtons: true
    };
  },
  methods: {
    ...mapMutations('report_list', ['change_lang']),
    ...mapMutations('story_board', ['update_selected_lv1', 'update_selected_lv2']),
    ...mapActions('story_board', ['fetch_viewer_sb_disclosures']),
    clickExportPdf() {
      this.$store.commit('story_board/update_print_mode', true);
      this.$bvModal.show('story-board-modal');
    },
    closeSearch() {
      this.toggleSearchBar();
      // this.$bvModal.hide('web-report-search-result');
      this.searchKeyword = '';
      this.searchResults = null;
      this.loading = false;
    },
    performSearch(searchword) {
      this.searchKeyword = searchword;
      this.loading = true;
      const keyword = this.searchKeyword || '';
      const results = [];
      if (!keyword.trim() || !this.sb_toc || !this.viewer_disclosure_map) {
        /* ... */ return;
      }

      this.$bvModal.show('web-report-search-result');
      const lowerCaseKeyword = keyword.toLowerCase();

      for (const lv1 of this.sb_toc) {
        const lv1Key = lv1.key;
        // 💡 변경: LV1을 건너뛸 경우, 그 이유를 콘솔에 경고로 표시합니다.
        if (!this.viewer_disclosure_map[lv1Key]) {
          continue;
        }
        if (!lv1.children || lv1.children.length === 0) {
          continue;
        }

        for (const lv2 of lv1.children) {
          const lv2Key = lv2.key;

          const disclosuresByOrder = this.viewer_disclosure_map[lv1Key]?.[lv2Key];

          // 💡 변경: LV2를 건너뛸 경우, 그 이유를 콘솔에 경고로 표시합니다.
          if (!disclosuresByOrder) {
            continue;
          }

          for (const orderIndex in disclosuresByOrder) {
            // 이하 검색 로직은 동일합니다.
            if (Object.prototype.hasOwnProperty.call(disclosuresByOrder, orderIndex)) {
              const disclosure = disclosuresByOrder[orderIndex];
              for (const propKey in disclosure) {
                if (Object.prototype.hasOwnProperty.call(disclosure, propKey)) {
                  const value = disclosure[propKey];
                  if (typeof value === 'string' && value.toLowerCase().includes(lowerCaseKeyword)) {
                    results.push({
                      lv1_info_key: lv1Key,
                      lv2_info_key: lv2Key,
                      order_index: orderIndex,
                      lv1_info: { key: lv1Key, name: lv1.name },
                      lv2_info: { key: lv2Key, name: lv2.name },
                      text: disclosure.text || `${value.substring(0, 100)}...`
                    });
                  }
                }
              }
            }
          }
        }
      }
      this.searchResults = results;
      this.loading = false;
    },
    isLv1Active(lv1Key) {
      return this.selected_lv1?.key === lv1Key;
    },

    isLv2Active(lv2Key) {
      return this.selected_lv2?.key === lv2Key;
    },
    go_sb_main() {
      window.scrollTo(0, 0);
      this.update_selected_lv1({ lv1_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });
      this.update_selected_lv2({ lv2_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });
      updateParamsFromQueryString('lv1', null, true);
      updateParamsFromQueryString('lv2', null, true);
      updateParamsFromQueryString('q_str', null, true);
    },
    showMenu() {
      this.isHoverMainMenu = true;
      this.showDropdown = true;
    },
    hideMenu() {
      this.showDropdown = true;
      this.isHoverMainMenu = false;
    },
    toggleDropdown() {
      this.isHoverMainMenu = !this.isHoverMainMenu;
    },
    toggleLang() {
      if (this.lang == 'KOR') {
        this.change_lang('ENG');
        document.querySelector('title').textContent = this.selected_report.report_name_e;
        updateParamsFromQueryString('l', 'ENG', false);
      } else {
        this.change_lang('KOR');
        document.querySelector('title').textContent = this.selected_report.report_name;
        updateParamsFromQueryString('l', null, false);
      }
    },
    update_lv1_info(lv1_info) {
      window.scrollTo(0, 0);
      this.update_selected_lv1({
        lv1_info: lv1_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });

      if (this.is_viewer) {
        this.update_selected_lv2({
          lv2_info: lv1_info.children[0],
          view_mode: this.is_viewer ? 'viewer' : 'editor'
        });
        updateParamsFromQueryString('lv2', lv1_info.children[0].name, true);
      } else {
        this.update_selected_lv2({
          lv2_info: null,
          view_mode: this.is_viewer ? 'viewer' : 'editor'
        });
        updateParamsFromQueryString('lv2', null, true);
      }

      updateParamsFromQueryString('lv1', lv1_info.name, true);

      if (!this.selected_viewer_disclosures) {
        this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code,
          selected_lv_info: {
            lv1_info: lv1_info,
            lv2_info: this.is_viewer ? lv1_info.children[0] : null
          }
        });
      }
    },
    update_lv2_info(lv1_info, lv2_info) {
      window.scrollTo(0, 0);
      this.update_selected_lv1({
        lv1_info: lv1_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });
      this.update_selected_lv2({
        lv2_info: lv2_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });

      updateParamsFromQueryString('lv1', lv1_info.name, true);
      updateParamsFromQueryString('lv2', lv2_info.name, true);

      if (!this.selected_viewer_disclosures) {
        this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code,
          selected_lv_info: {
            lv1_info: lv1_info,
            lv2_info: lv2_info
          }
        });
      }

      this.showDropdown = false;
    },
    handleHoverMenuItem(item) {
      this.hoveredMenuItem = item;
      this.$nextTick(() => {
        this.removeBorderFromOtherItems(item);
        this.updateTitleOpacity(item);
      });
    },
    handleHoverMenuItem2(item) {
      this.hoveredMenuItem = item;
      this.$nextTick(() => {
        this.updateTitleOpacity2(item);
      });
    },
    removeBorderFromOtherItems(currentItem) {
      const menuItems = document.querySelectorAll('.nav-item-custom');
      menuItems.forEach(menuItem => {
        if (menuItem.textContent.trim() !== currentItem) {
          menuItem.classList.remove('nav-active-custom');
        }
      });
    },
    resetHoverMenuItem() {
      this.hoveredMenuItem = null;
      this.resetTitleOpacity();
    },
    resetHoverMenuItem2() {
      this.hoveredMenuItem = null;
      this.resetTitleOpacity2();
    },
    updateTitleOpacity(activeItem) {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.nav-item-custom');
        titles.forEach(title => {
          if (title.textContent.trim() === activeItem) {
            title.style.opacity = '1';
          } else {
            title.style.opacity = '0.5';
          }
        });
      });
    },
    updateTitleOpacity2(activeItem) {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.submenu-link');
        titles.forEach(title => {
          if (title.textContent.trim() === activeItem) {
            title.style.opacity = '1';
          } else {
            title.style.opacity = '0.5';
          }
        });
      });
    },
    resetTitleOpacity() {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.nav-item-custom');
        titles.forEach(title => {
          title.style.opacity = '1';
        });
      });
    },
    resetTitleOpacity2() {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.submenu-link');
        titles.forEach(title => {
          title.style.opacity = '1';
        });
      });
    },
    handleHoverIcon(icon) {
      this.hoveredIcon = icon;
    },
    resetHoverIcon() {
      this.hoveredIcon = null;
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleSearchBar() {
      this.showSearchBar = !this.showSearchBar;
    },
    updateNavPointColor() {
      const lv1Count = this.sb_toc.length;
      const extra = Math.max(0, lv1Count - 5);
      const multiplier = 1 - 0.06 * extra;
      const offset = this.lang === 'ENG' ? 2 : 0;

      const fontSize1200 = Math.round(14 * multiplier) - offset;
      const fontSize1440 = Math.round(16 * multiplier) - offset;
      const fontSize1680 = Math.round(17 * multiplier) - offset;
      const fontSize1920 = Math.round(18 * multiplier) - offset;

      const submenuFontSize1200 = Math.round(13 * multiplier) - offset;
      const submenuFontSize1440 = Math.round(14 * multiplier) - offset;
      const submenuFontSize1680 = Math.round(15 * multiplier) - offset;
      const submenuFontSize1920 = Math.round(16 * multiplier) - offset;

      const styleElement = document.createElement('style');
      styleElement.innerHTML = `
          .nav-link-custom {
            padding-left: 0px;
            padding-right: 0px;
            position: relative;
            color: #111111;
            letter-spacing: -.017px;
            line-height: 1.15;
            text-align: center;
            padding-bottom: 3px;
            display: inline-block;
            transition: color 0.3s ease;
            font-weight: 600;

            @media screen and (min-width: 1200px) and (max-width: 1439.98px) {
              font-size: ${fontSize1200}px;
            }

            @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
              font-size: ${fontSize1440}px;
            }

            @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
              font-size: ${fontSize1680}px;
            }

            @media screen and (min-width: 1920px) {
              font-size: ${fontSize1920}px;
            }

            &::after {
              content: "";
              display: block;
              margin: 0 auto;
              width: 0;
              height: 3px;
              background-color: ${this.mainColor};
              transition: width 0.3s ease;
            }

            &:hover {
              color: ${this.mainColor} !important;
            }

            &:hover::after {
              width: 100%;
            }
          }
          .nav-link-custom-active {
            color: ${this.mainColor};
            &::after {
              content: "";
              display: block;
              margin: 0 auto;
              width: 100%;
              height: 3px;
              background-color: ${this.mainColor};
            }
          }
          .disabled-link-active{
            color: ${this.selected_report.sub_color};
          }
          .submenu-link {
            font-weight: 600;
            text-decoration: none;

            @media screen and (min-width: 1200px) and (max-width: 1439.98px) {
              font-size: ${submenuFontSize1200}px;
            }

            @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
              font-size: ${submenuFontSize1440}px;
            }

            @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
              font-size: ${submenuFontSize1680}px;
            }

            @media screen and (min-width: 1920px) {
              font-size: ${submenuFontSize1920}px;
            }

            padding-top: 0.3rem;
            padding-bottom: 0.3rem;
            color: #111111 !important;
          }
        `;
      document.head.appendChild(styleElement);
    },
    handleClosePreview() {
      if (this.globalString) {
        if (this.globalString === 'WriteStoryBoard' && this.selected_lv1 && this.selected_lv2) {
          // 만약 globalString 값이 'WriteStoryBoard'이면 해당 페이지로 이동
          this.$router.push({
            name: 'WriteStoryBoard',
            query: {
              lv1: this.selected_lv1.key,
              lv2: this.selected_lv2.key
            }
          });
        } else {
          this.$router.push({ name: this.globalString });
        }
      } else {
        // 기본적으로 CheckReport 페이지로 이동
        this.$router.push({ name: 'CheckReport' });
      }
    }
  },
  watch: {
    lang() {
      this.$nextTick(() => {
        this.updateNavPointColor();
      });
    },
    selected_lv1() {
      this.$nextTick(() => {
        this.updateNavPointColor();
      });
    }
  },
  mounted() {
    setTimeout(() => {
      this.disableButtons = false;
    }, 5000);
    this.updateNavPointColor();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style lang="scss" scoped>
.navbar-sb-wrapper {
  position: sticky;
  top: 0;
  z-index: 1050;
  background-color: white;
}

.navbar-sb {
  z-index: 1050;
  height: 90px;
  left: 50%;
  transform: translateX(-50%);
  transition: all 0.3s ease-in-out;
}

.navbar-sb-menu {
  display: flex;
  flex: 1 0 85%;
  max-width: 85%;
  transition: all 0.3s ease-in-out;

  &-expanded {
    flex: 1 0 100%;
    max-width: 100%;
  }
}

.nav-item-custom {
  flex: 1 1 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.nav-item-custom2 {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.nav-link-equal {
  text-align: center;
  width: 100%;
}

.submenu {
  display: flex;
  flex: 1 0 100%;
  position: fixed;
  top: -100%;
  transition: top 0.5s ease-in-out;
  background-color: #fff;
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  z-index: 1040;

  &-slide-down {
    top: 90px !important;
  }

  &-title {
    font-weight: 500;
    text-decoration: none;
    font-stretch: normal;
    letter-spacing: -0.017px;

    &:hover,
    &.submenu-active-custom {
      color: #0382df;
    }
  }

  &-link {
    font-weight: 500;
    text-decoration: none !important;

    &:hover,
    &.submenu-active,
    &.nuxt-link-active,
    &.nuxt-link-exact-active {
      color: #111111;
    }
  }

  .custom-col {
    position: relative;
    flex: 1;
  }
}

.navbar-submenu {
  border-left: 1px solid white;
  border-right: 1px solid white;
  transition: all 0.3s ease-in-out;

  &:hover {
    border-left: 1px solid lightgray;
    border-right: 1px solid lightgray;
  }
}

.link-container {
  padding-bottom: 8px;
  padding-top: 8px;
  line-height: 1.1;
}

.submenu-link {
  font-weight: 600;
  text-decoration: none;

  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: #111111 !important;

  &.submenu-active,
  &.nuxt-link-active,
  &.nuxt-link-exact-active {
    color: #111111;
  }

  &:hover {
    color: #111111 !important;
    opacity: 1 !important;
  }

  &.non-hovered {
    opacity: 0.5;
  }
}

.navbar-sb .text-secondary {
  color: rgba(17, 17, 17, 0.5);
  transition: color 0.3s ease, opacity 0.3s ease;

  &.hovered {
    color: #111111;
    opacity: 1;
  }

  &:hover {
    color: #111111;
    opacity: 1;
  }
}

@media screen and (max-width: 1920px) {
  .submenu {
    left: 0;
  }
}

@media screen and (min-width: 1921px) {
  .submenu {
    left: 50%;
    transform: translateX(-50%);
    max-width: 9000px;
  }
}

.disabled-link {
  pointer-events: none;
}

.navbar-sb {
  transition: all 0.3s ease-in-out;
}

.scrolled-navbar {
  border-bottom: 1px solid lightgray;
  position: fixed;
  transition: all 0.3s ease-in-out;
}

.search-input-group {
  border: 1px solid gray;
  border-radius: 25rem;
  overflow: hidden;
}

.search-input {
  border: none;
  border-radius: 0;
  padding: 10px 20px;
  font-size: 20px;
}

.search-input:focus {
  box-shadow: none;
}

.search-button {
  border: none;
  border-radius: 0;
  background-color: transparent;
  padding: 10px 20px;
}

.search-button b-icon {
  color: #fff;
  font-size: 20px;
}

.search-button:hover {
  background-color: #00000011;
}

#web-report-search-result {
  max-height: 600px;
}

.nav-link-custom,
.submenu-link {
  white-space: pre-line;
}
</style>
